import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../../services/api";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Image,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaImage,
  FaUserCircle,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import WebinarService from "../../../services/webinarService";
import axios from "axios";

const EditWebinar = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSeo, setShowSeo] = useState(false);

  // Image states
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState("");
  const [uploadingFeatured, setUploadingFeatured] = useState(false);

  const [instructorImage, setInstructorImage] = useState(null);
  const [instructorImagePreview, setInstructorImagePreview] = useState("");
  const [uploadingInstructor, setUploadingInstructor] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    title: "",
    short_description: "",
    long_description: "",
    featured_image: "",
    featured_image_public_id: "",
    instructor_name: "",
    instructor_bio: "",
    instructor_image: "",
    instructor_image_public_id: "",
    webinar_date: "",
    webinar_time: "",
    duration: "2 hours",
    meeting_link: "",
    recording_link: "",
    status: "upcoming",
    seo_title: "",
    seo_description: "",
  });

  useEffect(() => {
    loadWebinar();
  }, [id]);

  const loadWebinar = async () => {
    try {
      setLoading(true);
      const response = await WebinarService.adminGetWebinarById(id);
      const webinar = response.data;

      setFormData({
        title: webinar.title || "",
        short_description: webinar.short_description || "",
        long_description: webinar.long_description || "",
        featured_image: webinar.featured_image || "",
        featured_image_public_id: webinar.featured_image_public_id || "",
        instructor_name: webinar.instructor_name || "",
        instructor_bio: webinar.instructor_bio || "",
        instructor_image: webinar.instructor_image || "",
        instructor_image_public_id: webinar.instructor_image_public_id || "",
        webinar_date: webinar.webinar_date || "",
        webinar_time: webinar.webinar_time || "",
        duration: webinar.duration || "2 hours",
        meeting_link: webinar.meeting_link || "",
        recording_link: webinar.recording_link || "",
        status: webinar.status || "upcoming",
        seo_title: webinar.seo_title || "",
        seo_description: webinar.seo_description || "",
      });

      if (webinar.featured_image) {
        setFeaturedImagePreview(webinar.featured_image);
      }
      if (webinar.instructor_image) {
        setInstructorImagePreview(webinar.instructor_image);
      }
    } catch (error) {
      console.error("Failed to load webinar:", error);
      toast.error("Failed to load webinar details");
      navigate("/admin/webinars");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
      toast.error("Please upload JPG, PNG, or WEBP image");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2MB");
      return;
    }

    setUploadingFeatured(true);
    setFeaturedImage(file);
    setFeaturedImagePreview(URL.createObjectURL(file));

    const formDataUpload = new FormData();
    formDataUpload.append("image", file);
    formDataUpload.append("type", "webinar_featured");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/admin/upload-webinar-image`,
        formDataUpload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log("Upload response:", response.data);

      if (response.data.status && response.data.data) {
        setFormData((prev) => ({
          ...prev,
          featured_image: response.data.data.url,
          featured_image_public_id: response.data.data.public_id,
        }));
        toast.success("Featured image uploaded successfully");
      } else {
        toast.error(response.data.message || "Upload failed");
        setFeaturedImage(null);
        setFeaturedImagePreview("");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      console.error("Error response:", error.response);
      toast.error(error.response?.data?.message || "Failed to upload image");
      setFeaturedImage(null);
      setFeaturedImagePreview("");
    } finally {
      setUploadingFeatured(false);
    }
  };

  const handleInstructorImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
      toast.error("Please upload JPG, PNG, or WEBP image");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2MB");
      return;
    }

    setUploadingInstructor(true);
    setInstructorImage(file);
    setInstructorImagePreview(URL.createObjectURL(file));

    const formDataUpload = new FormData();
    formDataUpload.append("image", file);
    formDataUpload.append("type", "webinar_instructor");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/admin/upload-webinar-image`,
        formDataUpload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log("Upload response:", response.data);

      if (response.data.status && response.data.data) {
        setFormData((prev) => ({
          ...prev,
          instructor_image: response.data.data.url,
          instructor_image_public_id: response.data.data.public_id,
        }));
        toast.success("Instructor image uploaded successfully");
      } else {
        toast.error(response.data.message || "Upload failed");
        setInstructorImage(null);
        setInstructorImagePreview("");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(
        error.response?.data?.message || "Failed to upload instructor image",
      );
      setInstructorImage(null);
      setInstructorImagePreview("");
    } finally {
      setUploadingInstructor(false);
    }
  };

  const removeFeaturedImage = () => {
    setFeaturedImagePreview("");
    setFormData((prev) => ({
      ...prev,
      featured_image: "",
      featured_image_public_id: "",
    }));
  };

  const removeInstructorImage = () => {
    setInstructorImagePreview("");
    setFormData((prev) => ({
      ...prev,
      instructor_image: "",
      instructor_image_public_id: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      toast.error("Title is required");
      return;
    }
    if (!formData.instructor_name) {
      toast.error("Instructor name is required");
      return;
    }
    if (!formData.webinar_date) {
      toast.error("Webinar date is required");
      return;
    }
    if (!formData.webinar_time) {
      toast.error("Webinar time is required");
      return;
    }

    setSubmitting(true);
    try {
      await WebinarService.adminUpdateWebinar(id, formData);
      toast.success("Webinar updated successfully!");
      navigate("/admin/webinars");
    } catch (error) {
      console.error("Update failed:", error);
      toast.error(error.response?.data?.message || "Failed to update webinar");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading webinar details...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <Container fluid className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Button
              variant="link"
              className="text-decoration-none mb-2 p-0"
              onClick={() => navigate("/admin/webinars")}
            >
              <FaArrowLeft className="me-2" /> Back to Webinars
            </Button>
            <h2 className="mb-0">Edit Webinar</h2>
            <p className="text-muted">Update webinar information</p>
          </div>
        </div>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col lg={8}>
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">📋 Basic Information</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Short Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="short_description"
                      rows={2}
                      value={formData.short_description}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Long Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="long_description"
                      rows={8}
                      value={formData.long_description}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Featured Image</Form.Label>
                    <div className="border rounded p-3 text-center">
                      {featuredImagePreview ? (
                        <div className="mb-3">
                          <Image
                            src={featuredImagePreview}
                            alt="Featured preview"
                            style={{ maxHeight: "200px", maxWidth: "100%" }}
                            className="rounded"
                          />
                          <div className="mt-2">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={removeFeaturedImage}
                            >
                              Remove Image
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-3">
                          <FaImage size={50} className="text-muted mb-2" />
                          <p className="text-muted">No image selected</p>
                        </div>
                      )}
                      <Form.Control
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleFeaturedImageUpload}
                        disabled={uploadingFeatured}
                      />
                      {uploadingFeatured && (
                        <div className="mt-2">
                          <Spinner animation="border" size="sm" /> Uploading...
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>

              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">👨‍🏫 Instructor Information</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Instructor Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="instructor_name"
                      value={formData.instructor_name}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Instructor Bio</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="instructor_bio"
                      rows={3}
                      value={formData.instructor_bio}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Instructor Image</Form.Label>
                    <div className="border rounded p-3 text-center">
                      {instructorImagePreview ? (
                        <div className="mb-3">
                          <Image
                            src={instructorImagePreview}
                            alt="Instructor preview"
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                            }}
                            className="rounded-circle"
                          />
                          <div className="mt-2">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={removeInstructorImage}
                            >
                              Remove Image
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-3">
                          <FaUserCircle size={50} className="text-muted mb-2" />
                          <p className="text-muted">No image selected</p>
                        </div>
                      )}
                      <Form.Control
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleInstructorImageUpload}
                        disabled={uploadingInstructor}
                      />
                      {uploadingInstructor && (
                        <div className="mt-2">
                          <Spinner animation="border" size="sm" /> Uploading...
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-info text-white">
                  <h5 className="mb-0">📅 Schedule & Links</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Webinar Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="webinar_date"
                      value={formData.webinar_date}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Webinar Time *</Form.Label>
                    <Form.Control
                      type="time"
                      name="webinar_time"
                      value={formData.webinar_time}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Duration</Form.Label>
                    <Form.Control
                      type="text"
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Meeting Link</Form.Label>
                    <Form.Control
                      type="url"
                      name="meeting_link"
                      value={formData.meeting_link}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Recording Link</Form.Label>
                    <Form.Control
                      type="url"
                      name="recording_link"
                      value={formData.recording_link}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Status *</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      required
                    >
                      <option value="upcoming">📅 Upcoming</option>
                      <option value="live">🔴 Live</option>
                      <option value="completed">📹 Completed</option>
                    </Form.Select>
                  </Form.Group>
                </Card.Body>
              </Card>

              <Card className="mb-4 shadow-sm">
                <Card.Header
                  className="bg-secondary text-white"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowSeo(!showSeo)}
                >
                  <h5 className="mb-0">
                    {showSeo ? (
                      <FaChevronDown className="me-2" />
                    ) : (
                      <FaChevronRight className="me-2" />
                    )}
                    🔍 SEO Information (Optional)
                  </h5>
                </Card.Header>
                {showSeo && (
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>SEO Title</Form.Label>
                      <Form.Control
                        type="text"
                        name="seo_title"
                        value={formData.seo_title}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>SEO Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="seo_description"
                        rows={3}
                        value={formData.seo_description}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Card.Body>
                )}
              </Card>

              <Card className="shadow-sm">
                <Card.Body>
                  <div className="d-grid gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={submitting}
                    >
                      <FaSave className="me-2" />
                      {submitting ? "Updating..." : "Update Webinar"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="lg"
                      onClick={() => navigate("/admin/webinars")}
                    >
                      <FaTimes className="me-2" /> Cancel
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default EditWebinar;
